<script setup>
import { onMounted, ref, computed, watch } from 'vue'
import { createToaster } from '@meforma/vue-toaster'

definePageMeta({
  layout: 'dashboard',
  middleware: 'auth',
  ssr: false,
})

useHead({
  title: 'Verifikat',
})

const { $bgaApi } = useNuxtApp()

// Define reactive variables
const vouchers = ref([]);
const selectedVoucher = ref(null);
const selectedVoucherId = ref(null);
const allSelectedVoucher = ref([]);
const searchQuery = ref("");

// Updated filter structure to support multiple filters
const activeFilters = ref({
  transaction_date: {
    enabled: false,
    from: null,
    to: null,
  },
  merchant_id: {
    enabled: false,
    values: [],
  },
  voucher_series: {
    enabled: false,
    value: "",
  },
  voucher_type: {
    enabled: false,
    value: null,
  },
});

// Add backup of filters state for cancel functionality
const filtersBackup = ref(null);
const filtersOpen = ref(false);

const voucherModalOpen = ref(false);
const activeVoucherTab = ref('details');

// New ref for merchant ID input
const merchantIdInput = ref("");

const typingTimer = ref(null);
const exportType = ref("csv");
const toaster = process.client
  ? createToaster({ position: 'top-right' })
  : { success: () => {}, error: () => {}, info: () => {}, warning: () => {} }

const isSelectAllFiltered = ref(false);
const totalFilteredItems = ref(0);

const showSelectAllModal = ref(false);
const selectAllInProgress = ref(false);

const isExporting = ref(false);
const isFiltering = ref(false);

const merchantIds = ref([]);
const merchantOptions = computed(() =>
  (merchantIds.value || []).filter(
    (id) => !activeFilters.value.merchant_id.values.includes(id),
  ),
);

// State for pagination
const currentPage = ref(1);
const itemsPerPage = 50;
const totalItems = ref(0);
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage));
const voucherModalTitle = computed(() => {
  if (!selectedVoucher.value) return 'Verifikation';
  const series = selectedVoucher.value?.voucher_series || '';
  const number = selectedVoucher.value?.voucher_number || '';
  return `Verifikation: ${series}-${number}`;
});

// State for ordering
const orderingField = ref("-transaction_date");

// Flag to track if we should apply filters immediately
const applyFiltersImmediately = ref(false);

const onClickHandler = (page) => {
  allSelectedVoucher.value = [];
  currentPage.value = page;
  fetchVouchers(page);
};

const fetchVouchers = async (page = 1) => {
  isFiltering.value = true;
  try {
    // Build query parameters
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: itemsPerPage.toString(),
      search: searchQuery.value,
      ordering: orderingField.value,
    });

    if (
      activeFilters.value.transaction_date.enabled &&
      activeFilters.value.transaction_date.from &&
      activeFilters.value.transaction_date.to
    ) {
      params.append(
        "transaction_date_after",
        activeFilters.value.transaction_date.from
      );
      params.append(
        "transaction_date_before",
        activeFilters.value.transaction_date.to
      );
    }

    if (
      activeFilters.value.merchant_id.enabled &&
      activeFilters.value.merchant_id.values.length > 0
    ) {
      params.append(
        "merchant_ids",
        activeFilters.value.merchant_id.values.join(",")
      );
    }

    if (
      activeFilters.value.voucher_series.enabled &&
      activeFilters.value.voucher_series.value
    ) {
      params.append("voucher_series", activeFilters.value.voucher_series.value);
    }

    if (
      activeFilters.value.voucher_type.enabled &&
      activeFilters.value.voucher_type.value
    ) {
      params.append("voucher_type", activeFilters.value.voucher_type.value);
    }

    const response = await $bgaApi.get(`bga/vouchers/?${params.toString()}`)
    vouchers.value = response.data.results;
    totalItems.value = response.data.count;
  } catch (error) {
    toaster.error("Någonting gick fel");
  } finally {
    isFiltering.value = false;
  }
};

const fetchVouchersDetails = async (id) => {
  try {
    let url = `bga/vouchers/${id}`;
    const response = await $bgaApi.get(url)
    selectedVoucher.value = response.data;
  } catch (error) {
    toaster.error("Någonting gick fel");
  }
};

const fetchMerchantIds = async () => {
  try {
    let url = "bga/merchant-ids";
    const response = await $bgaApi.get(url)
    merchantIds.value = response.data?.data;
  } catch (error) {
    // console.error("Error fetching merchant IDs:", error);
  }
};

const selectAllVoucher = async (event) => {
  // If the checkbox is now checked AND there are no selections
  if (event.checked && allSelectedVoucher.value.length === 0) {
    if (totalPages.value > 1) {
      // Show modal for selecting scope
      showSelectAllModal.value = true;
    } else {
      // Just select current page
      isSelectAllFiltered.value = false;
      allSelectedVoucher.value = vouchers.value.map((voucher) => voucher.id);
    }
  }
  // If the checkbox is now unchecked OR there were previous selections
  else if (!event.checked || allSelectedVoucher.value.length > 0) {
    // Simply uncheck everything without showing modal
    isSelectAllFiltered.value = false;
    allSelectedVoucher.value = [];
  }
};

const handleSelectAllConfirm = async (selectAllPages) => {
  selectAllInProgress.value = true;
  try {
    if (selectAllPages) {
      // Select all filtered results across pages
      isSelectAllFiltered.value = true;
      const params = new URLSearchParams({
        search: searchQuery.value,
        page_size: totalItems.value,
      });

      // Add all active filters
      if (
        activeFilters.value.transaction_date.enabled &&
        activeFilters.value.transaction_date.from &&
        activeFilters.value.transaction_date.to
      ) {
        params.append(
          "transaction_date_after",
          activeFilters.value.transaction_date.from
        );
        params.append(
          "transaction_date_before",
          activeFilters.value.transaction_date.to
        );
      }

      if (
        activeFilters.value.merchant_id.enabled &&
        activeFilters.value.merchant_id.values.length > 0
      ) {
        params.append(
          "merchant_ids",
          activeFilters.value.merchant_id.values.join(",")
        );
      }

      if (
        activeFilters.value.voucher_series.enabled &&
        activeFilters.value.voucher_series.value
      ) {
        params.append(
          "voucher_series",
          activeFilters.value.voucher_series.value
        );
      }

      if (
        activeFilters.value.voucher_type.enabled &&
        activeFilters.value.voucher_type.value
      ) {
        params.append("voucher_type", activeFilters.value.voucher_type.value);
      }

      const response = await $bgaApi.get(
        `bga/vouchers/ids/?${params.toString()}`
      );
      allSelectedVoucher.value = response.data.ids;
      totalFilteredItems.value = response.data.total;
      toaster.success(`${totalFilteredItems.value} verifikationer valda`);
    } else {
      // Just select current page
      isSelectAllFiltered.value = false;
      allSelectedVoucher.value = vouchers.value.map((voucher) => voucher.id);
    }
  } catch (error) {
    toaster.error("Någonting gick fel");
    // Reset checkbox
    const mainCheckbox = document.querySelector("#flexCheckDefault");
    if (mainCheckbox) mainCheckbox.checked = false;
  } finally {
    selectAllInProgress.value = false;
    showSelectAllModal.value = false;
  }
};

const areAllCurrentPageItemsSelected = () => {
  const currentPageIds = new Set(vouchers.value.map((v) => v.id));
  return (
    vouchers.value.length > 0 &&
    allSelectedVoucher.value.length === vouchers.value.length &&
    allSelectedVoucher.value.every((id) => currentPageIds.has(id))
  );
};

// Update the watch for allSelectedVoucher to handle checkbox state
watch(
  allSelectedVoucher,
  (newVal) => {
    const mainCheckbox = document.querySelector("#flexCheckDefault");
    if (mainCheckbox) {
      mainCheckbox.checked = areAllCurrentPageItemsSelected();
      mainCheckbox.indeterminate =
        newVal.length > 0 && !areAllCurrentPageItemsSelected();
    }
  },
  { deep: true }
);

// Update the export function
const exportCSV = async () => {
  try {
    if (allSelectedVoucher.value.length === 0) {
      toaster.error("Vänligen välj verifikationer att exportera");
      return;
    }

    isExporting.value = true;

    let payload = {
      voucher_ids: allSelectedVoucher.value,
      response_type: exportType.value,
      is_filtered_export: isSelectAllFiltered.value,
      filters: isSelectAllFiltered.value
        ? {
            search: searchQuery.value,
            transaction_date_after: activeFilters.value.transaction_date.from,
            transaction_date_before: activeFilters.value.transaction_date.to,
            merchant_ids: activeFilters.value.merchant_id.values,
            voucher_series: activeFilters.value.voucher_series.value,
            voucher_type: activeFilters.value.voucher_type.value,
          }
        : null,
    };

    const response = await $bgaApi.post("bga/export-vouchers/", payload, {
      responseType: "blob",
    });

    if (exportType.value === "csv") {
      downloadCSV(response.data);
    } else {
      downloadExcel(response.data);
    }

    // Reset selections after successful export
    allSelectedVoucher.value = [];
    isSelectAllFiltered.value = false;

    // Reset main checkbox
    const mainCheckbox = document.querySelector("#flexCheckDefault");
    if (mainCheckbox) {
      mainCheckbox.checked = false;
      mainCheckbox.indeterminate = false;
    }
    toaster.success("Exporten är klar!");
  } catch (error) {
    // Handle the error case where the API returns a limit exceeded message
    if (error.response && error.response.data) {
      try {
        // Convert blob to text
        const blobText = await new Response(error.response.data).text();
        const errorData = JSON.parse(blobText);

        // Use the Swedish error message if available
        if (errorData.swe) {
          toaster.error(errorData.swe);
          return;
        } else if (errorData.eng) {
          toaster.error(errorData.eng);
          return;
        }
      } catch (parseError) {
        // console.log("Error parsing error response:", parseError);
      }
    }

    // Default error message if we couldn't parse the specific error
    toaster.error("Någonting gick fel under exporten");
  } finally {
    isExporting.value = false;
  }
};

const downloadExcel = async (data) => {
  const blob = new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "vouchers.xlsx");
};

const downloadCSV = (data) => {
  let blob = new Blob([data], { type: "text/csv" });
  saveAs(blob, "vouchers.csv");
};

const selectVoucher = async (voucher) => {
  selectedVoucherId.value = voucher.id;
  selectedVoucher.value = null;
  voucherModalOpen.value = true;
  await fetchVouchersDetails(voucher.id);
};

const selectSingleVoucher = (event, id) => {
  if (event.checked) {
    allSelectedVoucher.value.push(id);
  } else {
    const indexToRemove = allSelectedVoucher.value.findIndex((pl) => pl === id);
    allSelectedVoucher.value.splice(indexToRemove, 1);
  }
};

const searchVouchers = () => {
  clearTimeout(typingTimer.value);
  typingTimer.value = setTimeout(() => {
    fetchVouchers();
  }, 1000);
};

const handleSortClick = (field) => {
  orderingField.value = orderingField.value === field ? `-${field}` : field;
  fetchVouchers(currentPage.value);
};

// New functions for merchant ID multi-select
const addMerchantId = () => {
  if (
    merchantIdInput.value &&
    !activeFilters.value.merchant_id.values.includes(merchantIdInput.value)
  ) {
    activeFilters.value.merchant_id.values.push(merchantIdInput.value);
    merchantIdInput.value = "";
  }
};

const removeMerchantId = (id) => {
  const index = activeFilters.value.merchant_id.values.indexOf(id);
  if (index > -1) {
    activeFilters.value.merchant_id.values.splice(index, 1);
  }
};

const clearAllFilters = () => {
  Object.keys(activeFilters.value).forEach((key) => {
    activeFilters.value[key].enabled = false;
    if (key === "transaction_date") {
      activeFilters.value[key].from = null;
      activeFilters.value[key].to = null;
    } else if (key === "merchant_id") {
      activeFilters.value[key].values = [];
    } else {
      activeFilters.value[key].value = null;
    }
  });
  fetchVouchers(1);
  filtersOpen.value = false;
};

// Add function to backup filter state when modal opens
const backupFilters = () => {
  filtersBackup.value = JSON.parse(JSON.stringify(activeFilters.value));
};

const openFilters = () => {
  backupFilters();
  filtersOpen.value = true;
};

// Add function to restore filter state when cancel is clicked
const restoreFilters = () => {
  if (filtersBackup.value) {
    activeFilters.value = JSON.parse(JSON.stringify(filtersBackup.value));
  }
  filtersOpen.value = false;
};

const resetModalData = () => {
  selectedVoucher.value = null;
  selectedVoucherId.value = null;
  activeVoucherTab.value = 'details';
};

watch(voucherModalOpen, (isOpen) => {
  if (!isOpen) resetModalData();
});

const handleSelectAllCancel = () => {
  // Reset the main checkbox state
  const mainCheckbox = document.querySelector("#flexCheckDefault");
  if (mainCheckbox) {
    mainCheckbox.checked = false;
  }

  // Reset all selections
  allSelectedVoucher.value = [];
  isSelectAllFiltered.value = false;

  // Close the modal
  showSelectAllModal.value = false;
};

// Add dedicated function to apply filters
const applyFilters = () => {
  applyFiltersImmediately.value = true;
  fetchVouchers(1);
  applyFiltersImmediately.value = false;
  filtersOpen.value = false;
};

watch(
  activeFilters,
  () => {
    // Only fetch if we should apply filters immediately
    if (applyFiltersImmediately.value) {
      fetchVouchers(1);
    }
  },
  { deep: true }
);

// New function to select all available merchants
const selectAllMerchants = () => {
  if (merchantIds.value && merchantIds.value.length > 0) {
    // Add all merchant IDs that aren't already selected
    merchantIds.value.forEach((id) => {
      if (!activeFilters.value.merchant_id.values.includes(id)) {
        activeFilters.value.merchant_id.values.push(id);
      }
    });
  }
};

onMounted(() => {
  // Set applyFiltersImmediately to true for initial load only
  applyFiltersImmediately.value = true;
  fetchMerchantIds();
  fetchVouchers();
  // Then set it back to false so future filter changes don't trigger API calls
  applyFiltersImmediately.value = false;
});
</script>

<template>
  <div class="dashboard-stack">
    <div class="dashboard-header">
      <div class="dashboard-header__titles">
        <h1 class="dashboard-page__title">Verifikat</h1>
        <p class="dashboard-page__subtitle lp-body">
          Hantera verifikationer, filtrera och exportera underlag.
        </p>
      </div>
      <div class="dashboard-header__actions">
        <AppButton variant="secondary" size="sm" type="button" @click="openFilters">
          <AppIcon name="hero-adjustments-horizontal" size="md" />
          Filter
        </AppButton>
      </div>
    </div>

    <AppListHeader
      :search="searchQuery"
      :items-per-page="''"
      search-placeholder="Sök verifikat"
      @update:search="(value) => { searchQuery = value; searchVouchers(); }"
    />

    <div v-if="isFiltering" class="app-alert app-alert--info">
      Hämtar verifikat...
    </div>

    <AppCard class="app-card--table">
      <div class="app-table-wrap">
        <table class="app-table">
          <thead>
            <tr>
              <th>
                <label class="app-checkbox" aria-label="Markera alla">
                  <input
                    id="flexCheckDefault"
                    class="app-checkbox__input"
                    type="checkbox"
                    @change="selectAllVoucher($event.target)"
                  />
                </label>
              </th>
              <th @click="handleSortClick('payment_gateway')">BetalVäxel</th>
              <th @click="handleSortClick('merchant_id')">Handlar-ID</th>
              <th @click="handleSortClick('voucher_series')">Verifikationsserie</th>
              <th @click="handleSortClick('voucher_number')">Verifikationsnummer</th>
              <th @click="handleSortClick('transaction_date')">Transaktionsdatum</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="voucher in vouchers"
              :key="voucher.id"
              class="app-table__row--clickable"
              @click="selectVoucher(voucher)"
            >
              <td @click.stop>
                <label class="app-checkbox" aria-label="Markera verifikat">
                  <input
                    class="app-checkbox__input"
                    type="checkbox"
                    :checked="allSelectedVoucher.includes(voucher.id)"
                    @change="selectSingleVoucher($event.target, voucher.id)"
                  />
                </label>
              </td>
              <td class="app-table__cell-strong">{{ voucher.payment_gateway }}</td>
              <td class="app-table__cell-strong">{{ voucher.merchant_id }}</td>
              <td>{{ voucher.voucher_series }}</td>
              <td>{{ voucher.voucher_number }}</td>
              <td>{{ voucher.transaction_date }}</td>
            </tr>
            <tr v-if="!vouchers.length && !isFiltering">
              <td class="app-table__empty" colspan="6">Inga verifikat att visa.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="voucher-actions">
        <div class="voucher-actions__left">
          <AppSelect
            v-model="exportType"
            label="Exporttyp"
            class="voucher-actions__select"
            :options="[
              { value: 'csv', label: 'Exportera som CSV' },
              { value: 'excel', label: 'Exportera som Excel' },
            ]"
          />
          <AppButton
            variant="primary"
            size="sm"
            :loading="isExporting"
            :disabled="isExporting"
            @click="exportCSV"
          >
            Exportera
          </AppButton>
        </div>

        <div class="voucher-actions__right">
          <AppPagination
            :page="currentPage"
            :items-per-page="itemsPerPage"
            :total-items="totalItems"
            @update:page="onClickHandler"
          />
        </div>
      </div>
    </AppCard>

    <AppModal v-model="voucherModalOpen" :title="voucherModalTitle">
      <nav class="dashboard-tabs">
        <AppButton
          variant="secondary"
          size="sm"
          :class="activeVoucherTab === 'details' ? 'app-btn--tonal-primary' : 'app-btn--tonal-secondary'"
          @click="activeVoucherTab = 'details'"
        >
          Verifikationsdetaljer
        </AppButton>
        <AppButton
          variant="secondary"
          size="sm"
          :class="activeVoucherTab === 'orders' ? 'app-btn--tonal-primary' : 'app-btn--tonal-secondary'"
          @click="activeVoucherTab = 'orders'"
        >
          Inkluderade ordrar
        </AppButton>
      </nav>

      <div v-if="activeVoucherTab === 'details'" class="detail-kv">
        <div class="detail-kv__row">
          <span class="detail-kv__label">Beskrivning</span>
          <p class="detail-kv__value">{{ selectedVoucher?.payment_ref }}</p>
        </div>
        <div class="detail-kv__row">
          <span class="detail-kv__label">Verifikationsserie</span>
          <p class="detail-kv__value">{{ selectedVoucher?.voucher_series }}</p>
        </div>
        <div class="detail-kv__row">
          <span class="detail-kv__label">Verifikationsnummer</span>
          <p class="detail-kv__value">{{ selectedVoucher?.voucher_number }}</p>
        </div>
        <div class="detail-kv__row">
          <span class="detail-kv__label">Transaktionsdatum</span>
          <p class="detail-kv__value">{{ selectedVoucher?.transaction_date }}</p>
        </div>
        <div class="detail-kv__row">
          <span class="detail-kv__label">Verifikationstyp</span>
          <p class="detail-kv__value">{{ selectedVoucher?.voucher_type }}</p>
        </div>
      </div>

      <div v-else class="app-table-wrap voucher-modal-table">
        <table class="app-table">
          <thead>
            <tr>
              <th>Order-ID</th>
              <th>Kund-ID</th>
              <th>Lager-ID</th>
              <th>Moms applicerad</th>
              <th>Ordertyp</th>
              <th>Land</th>
              <th>Valuta</th>
              <th>Basbelopp</th>
              <th>SEK-belopp</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in selectedVoucher?.voucher_orders || []" :key="order.id">
              <td class="app-table__cell-strong">{{ order.kodmyran_order_id }}</td>
              <td>{{ order.kodmyran_user_id }}</td>
              <td>{{ order.warehouse_id }}</td>
              <td>{{ order.vat_applied ? 'Ja' : 'Nej' }}</td>
              <td>{{ order.order_type }}</td>
              <td>{{ order.country }}</td>
              <td>{{ order.currency }}</td>
              <td>{{ order.amount }}</td>
              <td>{{ order.sek_amount }}</td>
            </tr>
            <tr v-if="!(selectedVoucher?.voucher_orders || []).length">
              <td class="app-table__empty" colspan="9">Inga ordrar att visa.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <template #footer>
        <AppButton variant="secondary" size="sm" type="button" @click="voucherModalOpen = false">
          Stäng
        </AppButton>
      </template>
    </AppModal>

    <AppModal v-model="filtersOpen" title="Filtrera verifikat">
      <div class="voucher-filter-section">
        <label class="app-checkbox">
          <input
            type="checkbox"
            class="app-checkbox__input"
            v-model="activeFilters.transaction_date.enabled"
          />
          <span class="app-checkbox__label">Transaktionsdatum</span>
        </label>
        <div v-if="activeFilters.transaction_date.enabled" class="voucher-filter-fields">
          <AppTextField
            v-model="activeFilters.transaction_date.from"
            label="Från och med"
            type="date"
          />
          <AppTextField
            v-model="activeFilters.transaction_date.to"
            label="Till och med"
            type="date"
          />
        </div>
      </div>

      <div class="voucher-filter-section">
        <label class="app-checkbox">
          <input
            type="checkbox"
            class="app-checkbox__input"
            v-model="activeFilters.merchant_id.enabled"
          />
          <span class="app-checkbox__label">Handlar-ID</span>
        </label>
        <div v-if="activeFilters.merchant_id.enabled" class="voucher-filter-fields">
          <div class="voucher-merchant-row">
            <div class="app-field">
              <label class="app-field__label">Handlar-ID</label>
              <select class="app-input" v-model="merchantIdInput" @change="addMerchantId">
                <option value="">Välj Handlar-ID</option>
                <option v-for="id in merchantOptions" :key="id" :value="id">
                  {{ id }}
                </option>
              </select>
            </div>
            <AppButton variant="secondary" size="sm" type="button" @click="selectAllMerchants">
              Välj alla
            </AppButton>
          </div>
          <div class="voucher-merchant-tags">
            <AppChip
              v-for="id in activeFilters?.merchant_id?.values || []"
              :key="id"
              variant="info"
              class="voucher-merchant-chip"
            >
              {{ id }}
              <button type="button" class="voucher-merchant-remove" @click="removeMerchantId(id)">
                x
              </button>
            </AppChip>
          </div>
        </div>
      </div>

      <div class="voucher-filter-section">
        <label class="app-checkbox">
          <input
            type="checkbox"
            class="app-checkbox__input"
            v-model="activeFilters.voucher_series.enabled"
          />
          <span class="app-checkbox__label">Verifikationsserie</span>
        </label>
        <div v-if="activeFilters.voucher_series.enabled" class="voucher-filter-fields">
          <AppTextField
            v-model="activeFilters.voucher_series.value"
            label="Verifikationsserie"
            placeholder="Ange verifikationsserie"
          />
        </div>
      </div>

      <div class="voucher-filter-section">
        <label class="app-checkbox">
          <input
            type="checkbox"
            class="app-checkbox__input"
            v-model="activeFilters.voucher_type.enabled"
          />
          <span class="app-checkbox__label">Verifikationstyp</span>
        </label>
        <div v-if="activeFilters.voucher_type.enabled" class="voucher-filter-fields">
          <AppSelect
            v-model="activeFilters.voucher_type.value"
            label="Verifikationstyp"
            :options="[
              { value: 'payout', label: 'Utbetalning' },
              { value: 'sale', label: 'Försäljning' },
              { value: 'discrepancy', label: 'Diskrepans' },
              { value: 'fee', label: 'Avgifter' },
              { value: 'other', label: 'Annan' },
            ]"
          />
        </div>
      </div>

      <template #footer>
        <AppButton variant="secondary" size="sm" type="button" @click="restoreFilters">
          Avbryt
        </AppButton>
        <AppButton variant="outline" size="sm" type="button" @click="clearAllFilters">
          Rensa filter
        </AppButton>
        <AppButton variant="primary" size="sm" type="button" @click="applyFilters">
          Applicera
        </AppButton>
      </template>
    </AppModal>

    <AppModal v-model="showSelectAllModal" title="Välj omfattning">
      <p class="lp-body">
        Vill du markera alla verifikat över samtliga sidor eller endast aktuell sida?
      </p>
      <template #footer>
        <AppButton
          variant="primary"
          size="sm"
          type="button"
          :loading="selectAllInProgress"
          :disabled="selectAllInProgress"
          @click="handleSelectAllConfirm(true)"
        >
          Markera alla över samtliga sidor
        </AppButton>
        <AppButton
          variant="secondary"
          size="sm"
          type="button"
          :disabled="selectAllInProgress"
          @click="handleSelectAllConfirm(false)"
        >
          Markera endast aktuell sida
        </AppButton>
        <AppButton
          variant="outline"
          size="sm"
          type="button"
          :disabled="selectAllInProgress"
          @click="handleSelectAllCancel"
        >
          Avbryt
        </AppButton>
      </template>
    </AppModal>
  </div>
</template>

<style scoped>
.voucher-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lp-space-4);
  padding: var(--lp-space-4) var(--lp-space-5);
  border-top: 1px solid var(--lp-dashboard-border);
  background: var(--lp-dashboard-hover);
  flex-wrap: wrap;
}

.voucher-actions__left {
  display: flex;
  align-items: flex-end;
  gap: var(--lp-space-3);
  flex-wrap: wrap;
}

.voucher-actions__right {
  margin-left: auto;
}

.voucher-actions__select {
  min-width: 220px;
}

.voucher-actions__right .app-pagination {
  border-top: 0;
  padding: 0;
}

.voucher-filter-section {
  display: flex;
  flex-direction: column;
  gap: var(--lp-space-3);
  margin-bottom: var(--lp-space-4);
}

.voucher-filter-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--lp-space-3);
}

.voucher-merchant-row {
  display: flex;
  align-items: flex-end;
  gap: var(--lp-space-3);
  flex-wrap: wrap;
}

.voucher-merchant-tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--lp-space-2);
}

.voucher-merchant-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.voucher-merchant-remove {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}

.voucher-modal-table {
  margin-top: var(--lp-space-4);
}
</style>
